import { useInstallPrompt } from "@/pwa";
import { Download, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Subtle install chip. Only renders when the browser has surfaced a
 * beforeinstallprompt event AND the app is not already running standalone.
 * Variants:
 *   - "drawer"  — full-width row for the mobile nav drawer
 *   - "settings"— card row for Settings page
 *   - "chip"    — inline chip
 */
export default function InstallAlphaCorePrompt({ variant = "chip" }) {
  const { available, standalone, promptInstall } = useInstallPrompt();
  if (standalone || !available) return null;

  const doInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") toast.success("AlphaCore is installing.");
  };

  if (variant === "drawer") {
    return (
      <button
        onClick={doInstall}
        className="w-full flex items-center gap-3 px-3 py-3 rounded-md border border-white/10 hover:bg-white/[0.03] text-left min-h-[52px]"
        data-testid="install-app-drawer"
      >
        <div className="w-9 h-9 rounded-md border border-white/10 flex items-center justify-center" style={{ background: "rgba(138,82,255,0.10)" }}>
          <Smartphone className="h-4 w-4" style={{ color: "rgb(var(--ac-purple))" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Install AlphaCore</div>
          <div className="text-xs text-white/50 truncate">One-tap access from your home screen</div>
        </div>
        <Download className="h-4 w-4 text-white/40" />
      </button>
    );
  }

  if (variant === "settings") {
    return (
      <button
        onClick={doInstall}
        className="w-full kr-card p-6 flex items-center gap-4 hover:bg-white/[0.02] transition-colors text-left"
        data-testid="install-app-settings"
      >
        <div className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center">
          <Smartphone className="h-5 w-5" style={{ color: "rgb(var(--ac-purple))" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="kr-overline" style={{ color: "rgb(var(--ac-purple))" }}>[ PWA ]</div>
          <div className="font-display text-xl mt-1">Install AlphaCore</div>
          <div className="text-sm text-white/50 mt-1">Install AlphaCore as a native app on this device — no App Store required.</div>
        </div>
        <Download className="h-5 w-5 text-white/30 shrink-0" />
      </button>
    );
  }

  return (
    <Button
      onClick={doInstall}
      variant="outline"
      size="sm"
      className="border-white/10 hover:bg-white/5 hover:text-white"
      data-testid="install-app-chip"
    >
      <Download className="h-3.5 w-3.5 mr-1.5" /> Install app
    </Button>
  );
}
